import { AvailableLocale } from '@/i18n/types'
import { RankingParams } from '@/types'

export const LOCALE_COOKIE = 'selected_locale'
// used to be used for storing clid + windget_id, hence the cookie name "wid"
export const LANDING_CLID_COOKIE = 'landing_wid'
export const RANKING_CLUSTER_ID_COOKIE = 'rcid'
export const RANKING_AUDIENCE_COOKIE = 'audnc3' // number means we did reset audience for all users
const SESSION_COOKIE = 'pgsession'
export const PLATFORM_COOKIE = 'pg_plt'
export const AUID_COOKIE = 'auid'
export const UAC_COOKIE = 'pg_uac'
export const YAHOO_ID_SIGNAL_COOKIE = 'yahooId'
const SDK_UAC_COOKIE = 'pg_sdk_uac'
export const HASHED_EMAIL_COOKIE = 'pg_hue'

const hour = 1000 * 60 * 60
const day = 24 * hour

export function parseCookie(cookie: string) {
    return cookie.split('; ').reduce<Record<string, string>>((acc, el) => {
        const [key = '', value = ''] = el.split('=')
        acc[key] = decodeURIComponent(value)
        return acc
    }, {})
}

export function updateCookie(key: string, value: string, ttl: number) {
    const expires = new Date()
    expires.setTime(expires.getTime() + ttl)

    document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; Secure; SameSite=none`
}

export function deleteCookie(key: string) {
    updateCookie(key, '', -1)
}

function getEncodedCookieJSON(cookie: string, cookieName: string): Record<string, string> | null {
    const cookies = parseCookie(cookie)
    const rawValue = cookies[cookieName]
    if (!rawValue) {
        return null
    }

    try {
        return JSON.parse(atob(rawValue)) as Record<string, string>
    } catch {
        return null
    }
}

export function getUACFromCookies(cookie: string, cookieName: string) {
    return getEncodedCookieJSON(cookie, cookieName) || {}
}

export function getUACFromCookies30d(cookie: string) {
    return getUACFromCookies(cookie, UAC_COOKIE)
}

export function getCurrentLocaleFromCookies(cookie: string): string | undefined {
    const cookies = parseCookie(cookie)
    return cookies[LOCALE_COOKIE]
}

export function getLandingClidFromCookies(cookie: string): string | undefined {
    const cookies = parseCookie(cookie)
    return cookies[LANDING_CLID_COOKIE]
}

export function updateLocaleCookie(value: AvailableLocale) {
    // Set expiration date (1 year from now)
    updateCookie(LOCALE_COOKIE, value, 365 * day)
}

export function updateLandingClidCookie(value: string) {
    // 30 days
    updateCookie(LANDING_CLID_COOKIE, value, 30 * day)
}

export function getAUID(cookie: string) {
    return parseCookie(cookie).auid
}

export function getRankingParamsFromCookies(cookie: string): RankingParams {
    const parsedCookie = parseCookie(cookie)
    return {
        clusterId: parsedCookie[RANKING_CLUSTER_ID_COOKIE],
        audience: parsedCookie[RANKING_AUDIENCE_COOKIE],
    }
}

export function updateRankingCookies({ clusterId, audience }: RankingParams) {
    if (clusterId) {
        updateCookie(RANKING_CLUSTER_ID_COOKIE, clusterId, 1 * hour)
    } else {
        deleteCookie(RANKING_CLUSTER_ID_COOKIE)
    }

    if (audience) {
        updateCookie(RANKING_AUDIENCE_COOKIE, audience, 30 * day)
    } else {
        deleteCookie(RANKING_AUDIENCE_COOKIE)
    }
}

export function getSessionCookie(): string | undefined {
    return parseCookie(document.cookie)[SESSION_COOKIE]
}

export function getPlatformFromCookies(cookie: string): string | undefined {
    const cookies = parseCookie(cookie)
    return cookies[PLATFORM_COOKIE]
}

export function setSessionCookie(sessionId: string) {
    const SESSION_INACTIVITY_TTL = hour / 2
    updateCookie(SESSION_COOKIE, sessionId, SESSION_INACTIVITY_TTL)
}

export function getSDKUACFromCookies(cookie: string): Record<string, string> | null {
    return getEncodedCookieJSON(cookie, SDK_UAC_COOKIE)
}

export function updateSDKUACCookie(value: Record<string, string>) {
    updateCookie(SDK_UAC_COOKIE, btoa(JSON.stringify(value)), 30 * day)
}

export function getHashedEmailFromCookie(): string | undefined {
    return parseCookie(document.cookie)[HASHED_EMAIL_COOKIE]
}

export function updateHashedEmailCookie(hashedEmail: string) {
    updateCookie(HASHED_EMAIL_COOKIE, hashedEmail, 30 * day)
}
